import React, { useState, useEffect, useCallback } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ComposedChart,
  PieChart,
  Pie,
  Area,
  AreaChart,
} from "recharts";

import {
  TrendingUp,
  Calendar,
  Users,
  Building2,
  MapPin,
  ArrowUp,
  FileText,
  Filter,
  Award,
} from "lucide-react";
import axios from "axios";
// import { API_ELSTIC_URL } from "../../config";
// import { extractProprietorName } from "../../utils/utils";
import {
  Collapse,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Autocomplete,
  Button,
  Skeleton,
  Box,
} from "@mui/material";
import { Link } from "react-router-dom";
import { API_ELSTIC_URL } from "../../config";
import { extractProprietorName } from "../../../utils/utils";

// const API_ELSTIC_URL = 'your-api-url-here'; // Replace with your actual API URL

// Debounce function
function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

const COLORS = [
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#f59e0b",
  "#10b981",
  "#06b6d4",
  "#6366f1",
  "#a855f7",
];

const ManageTrademarkTrends = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [yearRange, setYearRange] = useState({
    from: currentYear - 4,
    to: currentYear,
  });
  const [selectedMonth, setSelectedMonth] = useState(currentMonth - 1 || 12);
  const [selectedYear, setSelectedYear] = useState(
    currentMonth === 1 ? currentYear - 1 : currentYear
  );
  const [tabValue, setTabValue] = useState(0);

  // Loading states
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [loadingFilings, setLoadingFilings] = useState(false);
  const [loadingObjection, setLoadingObjection] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [loadingTopAttorneys, setLoadingTopAttorneys] = useState(false);
  const [loadingTopBrands, setLoadingTopBrands] = useState(false);

  // Data states
  const [monthlyData, setMonthlyData] = useState(null);
  const [filingsApprovalsData, setFilingsApprovalsData] = useState(null);
  const [objectionOppositionData, setObjectionOppositionData] = useState(null);
  const [stateData, setStateData] = useState(null);
  const [topAttorneys, setTopAttorneys] = useState(null);
  const [topBrands, setTopBrands] = useState(null);
  const [topAttorneys50, setTopAttorneys50] = useState(null);
  const [topBrands50, setTopBrands50] = useState(null);

  // Advanced filter states
  const [openFilter, setOpenFilter] = useState(false);
  const [filterType, setFilterType] = useState("proprietor");
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedFilterValue, setSelectedFilterValue] = useState("");

  // ================= API: Suggestions =================
  const fetchSuggestions = useCallback(
    debounce(async (query) => {
      if (!query) return setSuggestions([]);
      try {
        const endpoint =
          filterType === "proprietor"
            ? `${API_ELSTIC_URL}/attorney/get-proprietor-name?proprietor_name=${query}`
            : `${API_ELSTIC_URL}/attorney/get-attorney-name?attorney_name=${query}`;
        const response = await axios.get(endpoint);
        setSuggestions(response.data.data || []);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSuggestions([]);
      }
    }, 500),
    [filterType]
  );

  useEffect(() => {
    fetchSuggestions(searchQuery);
  }, [searchQuery, fetchSuggestions]);

  const handleResetFilter = () => {
    setSelectedFilterValue("");
    setSearchQuery("");
    setOpenFilter(false);
  }

  // ================= API Calls =================
  const commonParams =
    filterType === "proprietor"
      ? { proprietor_name: selectedFilterValue }
      : filterType === "attorney"
      ? { attorney_name: selectedFilterValue }
      : {};

  // useEffect(() => {
  //   fetchAllData();
  // }, [yearRange, selectedMonth, selectedYear]);

  // const fetchAllData = async () => {
  //   setLoading(true);
  //   try {
  //     await Promise.all([
  //       fetchMonthlyTrends(),
  //       fetchFilingsApprovals(),
  //       fetchObjectionOpposition(),
  //       fetchStateData(),
  //       fetchTopAttorneys(10),
  //       fetchTopBrands(10),
  //       fetchTopAttorneys(50),
  //       fetchTopBrands(50)
  //     ]);
  //   } catch (error) {
  //     console.error('Error fetching data:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const fetchMonthlyTrends = async () => {
  //   // Actual API call:
  //   const response = await axios.get(`${API_ELSTIC_URL}/elastic/get-monthly-filings-trend?month=${selectedMonth}&year=${selectedYear}`);
  //   setMonthlyData(response.data.data);

  //   // setTimeout(() => {
  //   //   setMonthlyData({
  //   //     period: { month: 'September', month_number: 9, year: 2025 },
  //   //     daily_breakdown: Array.from({ length: 30 }, (_, i) => ({
  //   //       date: `2025-09-${String(i + 1).padStart(2, '0')}`,
  //   //       total_filings: Math.floor(Math.random() * 1000) + 1500
  //   //     })),
  //   //     summary: {
  //   //       total_filings: 65432,
  //   //       avg_filings_per_day: 2181,
  //   //       peak_day: { date: '2025-09-17', filings: 2452 }
  //   //     }
  //   //   });
  //   // }, 100);
  // };

  // const fetchFilingsApprovals = async () => {
  //   const response = await axios.get(`${API_ELSTIC_URL}/elastic/get-filings-approvals-data?from_year=${yearRange.from}&to_year=${yearRange.to}`);
  //   setFilingsApprovalsData(response.data.data);
  // };

  // const fetchObjectionOpposition = async () => {
  //   const response = await axios.get(`${API_ELSTIC_URL}/elastic/get-objection-opposition-data?from_year=${yearRange.from}&to_year=${yearRange.to}`);
  //   setObjectionOppositionData(response.data.data);
  // };

  // const fetchStateData = async () => {
  //   const response = await axios.get(`${API_ELSTIC_URL}/elastic/get-filings-by-state?from_year=${yearRange.from}&to_year=${yearRange.to}`);
  //   setStateData(response.data.data);
  // };

  // const fetchTopAttorneys = async (limit) => {
  //   try {
  //     // Actual API call with axios
  //     const response = await axios.get(`${API_ELSTIC_URL}/elastic/get-all-trademark-data`, {
  //       params: {
  //         search: 'attorney_name',
  //         from_year: yearRange.from,
  //         to_year: yearRange.to,
  //         limit: limit
  //       }
  //     });

  //     // Extract the aggregations data from API response
  //     // Assuming the API returns: { success: true, data: { aggregations: [...] } }
  //     const aggregationsData = response.data.data.aggregations;

  //     // Map the API data to match our component's expected format
  //     const formattedData = {
  //       aggregations: aggregationsData.map(item => ({
  //         attorney_name: item.attorney_name || item.key?.split('[')[0] || 'Unknown',
  //         doc_count: item.doc_count || 0
  //       }))
  //     };

  //     // Set the appropriate state based on limit
  //     if (limit === 10) {
  //       setTopAttorneys(formattedData);
  //     } else {
  //       setTopAttorneys50(formattedData);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching top attorneys:', error);
  //     // Set empty data on error
  //     if (limit === 10) {
  //       setTopAttorneys({ aggregations: [] });
  //     } else {
  //       setTopAttorneys50({ aggregations: [] });
  //     }
  //   }
  // };

  // const fetchTopBrands = async (limit) => {
  //   try {
  //     // Actual API call with axios
  //     const response = await axios.get(`${API_ELSTIC_URL}/elastic/get-all-trademark-data`, {
  //       params: {
  //         search: 'proprietor_name',
  //         from_year: yearRange.from,
  //         to_year: yearRange.to,
  //         limit: limit
  //       }
  //     });

  //     // Extract the aggregations data from API response
  //     // Assuming the API returns: { success: true, data: { aggregations: [...] } }
  //     const aggregationsData = response.data.data.aggregations;

  //     // Map the API data to match our component's expected format
  //     const formattedData = {
  //       aggregations: aggregationsData.map(item => ({
  //         attorney_name: item.attorney_name || item.key?.split('\\n')[0] || 'Unknown',
  //         doc_count: item.doc_count || 0
  //       }))
  //     };

  //     // Set the appropriate state based on limit
  //     if (limit === 10) {
  //       setTopBrands(formattedData);
  //     } else {
  //       setTopBrands50(formattedData);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching top brands:', error);
  //     // Set empty data on error
  //     if (limit === 10) {
  //       setTopBrands({ aggregations: [] });
  //     } else {
  //       setTopBrands50({ aggregations: [] });
  //     }
  //   }
  // };

  const fetchMonthlyTrends = async () => {
    setLoadingMonthly(true);
    try {
      const res = await axios.get(
        `${API_ELSTIC_URL}/elastic/get-monthly-filings-trend`,
        {
          params: { month: selectedMonth, year: selectedYear, ...commonParams },
        }
      );
      setMonthlyData(res.data.data);
    } finally {
      setLoadingMonthly(false);
    }
  };

  const fetchFilingsApprovals = async () => {
    setLoadingFilings(true);
    try {
      const res = await axios.get(
        `${API_ELSTIC_URL}/elastic/get-filings-approvals-data`,
        {
          params: {
            from_year: yearRange.from,
            to_year: yearRange.to,
            ...commonParams,
          },
        }
      );
      setFilingsApprovalsData(res.data.data);
    } finally {
      setLoadingFilings(false);
    }
  };

  const fetchObjectionOpposition = async () => {
    setLoadingObjection(true);
    try {
      const res = await axios.get(
        `${API_ELSTIC_URL}/elastic/get-objection-opposition-data`,
        {
          params: {
            from_year: yearRange.from,
            to_year: yearRange.to,
            ...commonParams,
          },
        }
      );
      setObjectionOppositionData(res.data.data);
    } finally {
      setLoadingObjection(false);
    }
  };

  const fetchStateData = async () => {
    setLoadingState(true);
    try {
      const res = await axios.get(
        `${API_ELSTIC_URL}/elastic/get-filings-by-state`,
        {
          params: {
            from_year: yearRange.from,
            to_year: yearRange.to,
            ...commonParams,
          },
        }
      );
      setStateData(res.data.data);
    } finally {
      setLoadingState(false);
    }
  };

  const fetchTopAttorneys = async (limit) => {
    setLoadingTopAttorneys(true);
    try {
      const res = await axios.get(
        `${API_ELSTIC_URL}/elastic/get-all-trademark-data`,
        {
          params: {
            search: "attorney_name",
            from_year: yearRange.from,
            to_year: yearRange.to,
            limit,
            // query: filterType === "attorney" ? selectedFilterValue : "",
          },
        }
      );
      const aggregationsData = res.data.data.aggregations || [];
      // console.log("aggregationsData", aggregationsData);
      const formattedData = {
        aggregations: aggregationsData.map((item) => ({
          attorney_name:
            item.attorney_name || item.key?.split("[")[0] || "Unknown",
          doc_count: item.doc_count || 0,
          key: item.key,
        })),
      };
      if (limit === 10) setTopAttorneys(formattedData);
      else setTopAttorneys50(formattedData);
    } finally {
      setLoadingTopAttorneys(false);
    }
  };

  const fetchTopBrands = async (limit) => {
    setLoadingTopBrands(true);
    try {
      const res = await axios.get(
        `${API_ELSTIC_URL}/elastic/get-all-trademark-data`,
        {
          params: {
            search: "proprietor_name",
            from_year: yearRange.from,
            to_year: yearRange.to,
            limit,
            // query: filterType === "proprietor" ? selectedFilterValue : "",
          },
        }
      );
      const aggregationsData = res.data.data.aggregations || [];
      const formattedData = {
        aggregations: aggregationsData.map((item) => ({
          attorney_name:
            item.attorney_name || item.key?.split("\\n")[0] || "Unknown",
          doc_count: item.doc_count || 0,
          key: item.key,
        })),
      };
      if (limit === 10) setTopBrands(formattedData);
      else setTopBrands50(formattedData);
    } finally {
      setLoadingTopBrands(false);
    }
  };

  // ================= Fetch All =================
  useEffect(() => {
    if (!selectedFilterValue && openFilter) return; // only fetch after filter applied
    fetchMonthlyTrends();
    fetchFilingsApprovals();
    fetchObjectionOpposition();
    fetchStateData();
    fetchTopAttorneys(10);
    fetchTopBrands(10);
    fetchTopAttorneys(50);
    fetchTopBrands(50);
  }, [yearRange, selectedMonth, selectedYear, selectedFilterValue, filterType]);

  // ================= UI: Tooltip =================

  // ================= UI: Tooltip =================
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div
          style={{
            background: "#fff",
            padding: "10px",
            border: "1px solid #ddd",
            borderRadius: "8px",
          }}
        >
          <p style={{ fontWeight: 600 }}>{label}</p>
          {payload.map((entry, i) => (
            <p key={i} style={{ color: entry.color, fontWeight: 500 }}>
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // if (loading) {
  //   return (
  //     <div className="main-content">
  //       <div className="page-content">
  //         <div className="container-fluid">
  //           <div style={{
  //             display: 'flex',
  //             justifyContent: 'center',
  //             alignItems: 'center',
  //             minHeight: '70vh',
  //             flexDirection: 'column',
  //             gap: '20px'
  //           }}>
  //             <div style={{
  //               width: '80px',
  //               height: '80px',
  //               border: '6px solid #e5e7eb',
  //               borderTop: '6px solid #3b82f6',
  //               borderRadius: '50%',
  //               animation: 'spin 1s linear infinite'
  //             }} />
  //             <p style={{ fontSize: '18px', color: '#64748b', fontWeight: 500 }}>Loading Trademark Analytics...</p>
  //             <style>{`
  //               @keyframes spin {
  //                 0% { transform: rotate(0deg); }
  //                 100% { transform: rotate(360deg); }
  //               }
  //             `}</style>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="main-content">
      <div
        className="page-content"
        style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}
      >
        <div className="container-fluid" style={{ padding: "24px" }}>
          {/* Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "20px",
              padding: "40px",
              marginBottom: "32px",
              color: "white",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginBottom: "12px",
              }}
            >
              <TrendingUp size={48} />
              <h1 style={{ fontSize: "36px", fontWeight: 800 }}>
                Trademark Data Trends{" "}
                {selectedFilterValue && (
                  <span style={{ fontSize: "20px", fontWeight: 500 }}>
                    (Filtered by {filterType}: {selectedFilterValue})
                  </span>
                )}
              </h1>
            </div>
          </div>

          {/* Year Range Filter */}
          <div style={{ marginBottom: "32px" }}>
            <div
              className="card"
              style={{
                borderRadius: "16px",
                border: "2px solid #e5e7eb",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              }}
            >
              <div className="card-body" style={{ padding: "24px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "24px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Filter size={24} color="white" />
                    </div>
                    <strong style={{ fontSize: "18px", color: "#1e293b" }}>
                      Filter by Year Range:
                    </strong>
                  </div>
                  <select
                    className="form-control"
                    style={{
                      width: "160px",
                      padding: "12px 16px",
                      borderRadius: "10px",
                      border: "2px solid #e5e7eb",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                    value={yearRange.from}
                    onChange={(e) =>
                      setYearRange({
                        ...yearRange,
                        from: parseInt(e.target.value),
                      })
                    }
                  >
                    {Array.from(
                      { length: 10 },
                      (_, i) => currentYear - 9 + i
                    ).map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "18px",
                      color: "#64748b",
                    }}
                  >
                    to
                  </span>
                  <select
                    className="form-control"
                    style={{
                      width: "160px",
                      padding: "12px 16px",
                      borderRadius: "10px",
                      border: "2px solid #e5e7eb",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                    value={yearRange.to}
                    onChange={(e) =>
                      setYearRange({
                        ...yearRange,
                        to: parseInt(e.target.value),
                      })
                    }
                  >
                    {Array.from(
                      { length: 10 },
                      (_, i) => currentYear - 9 + i
                    ).map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="contained"
                    onClick={() => setOpenFilter(!openFilter)}
                  >
                    Advanced Filter
                  </Button>
                </div>
                <div>
                  {/* Advanced Filter Collapse */}
                  <Collapse in={openFilter}>
                    <Box
                      sx={{
                        p: 2,
                        border: "1px solid #ddd",
                        borderRadius: "12px",
                        mb: 3,
                        background: "#fff",
                        mt: 2,
                      }}
                    >
                      <RadioGroup
                        row
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                      >
                        <FormControlLabel
                          value="proprietor"
                          control={<Radio />}
                          label="Proprietor Search"
                        />
                        <FormControlLabel
                          value="attorney"
                          control={<Radio />}
                          label="Attorney Search"
                        />
                      </RadioGroup>

                      <Autocomplete
                        freeSolo
                        options={suggestions}
                        onInputChange={(e, newValue) =>
                          setSearchQuery(newValue)
                        }
                        onChange={(e, value) => setSelectedFilterValue(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`Enter ${filterType} name`}
                            fullWidth
                          />
                        )}
                      />

                      <Button
                        sx={{ mt: 2 }}
                        variant="contained"
                        disabled={!selectedFilterValue}
                      >
                        Search
                      </Button>

                       <Button
                        sx={{ mt: 2, ml: 2 }}
                        variant="contained"
                        // disabled={!selectedFilterValue}
                        color="secondary"
                        onClick={handleResetFilter}
                      >
                        Reset
                      </Button>
                    </Box>
                  </Collapse>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Filing Trends */}
          <div style={{ marginBottom: "32px" }}>
            <div
              className="card"
              style={{
                borderRadius: "16px",
                border: "2px solid #e5e7eb",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              }}
            >
              <div className="card-body" style={{ padding: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    marginBottom: "28px",
                    flexWrap: "wrap",
                    gap: "20px",
                  }}
                >
                  <div>
                    <h2
                      style={{
                        fontSize: "28px",
                        fontWeight: 700,
                        marginBottom: "8px",
                        color: "#1e293b",
                      }}
                    >
                      Monthly Filing Trends for {monthlyData?.period.month}{" "}
                      {monthlyData?.period.year}
                    </h2>
                    <p
                      style={{ color: "#64748b", margin: 0, fontSize: "16px" }}
                    >
                      Daily trademark filing patterns for{" "}
                      {monthlyData?.period.month} {monthlyData?.period.year}
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <select
                      className="form-control"
                      style={{
                        padding: "10px 16px",
                        borderRadius: "10px",
                        border: "2px solid #e5e7eb",
                        fontWeight: 600,
                        width: "160px",
                      }}
                      value={selectedMonth}
                      onChange={(e) =>
                        setSelectedMonth(parseInt(e.target.value))
                      }
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(2000, i).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-control"
                      style={{
                        padding: "10px 16px",
                        borderRadius: "10px",
                        border: "2px solid #e5e7eb",
                        fontWeight: 600,
                      }}
                      value={selectedYear}
                      onChange={(e) =>
                        setSelectedYear(parseInt(e.target.value))
                      }
                    >
                      {Array.from(
                        { length: 10 },
                        (_, i) => currentYear - 9 + i
                      ).map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Summary Cards */}
                <div className="row mb-4">
                  <div className="col-md-4 mb-3">
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        borderRadius: "16px",
                        padding: "24px",
                        position: "relative",
                        overflow: "hidden",
                        boxShadow: "0 8px 30px rgba(102, 126, 234, 0.3)",
                      }}
                    >
                      <div style={{ position: "relative", zIndex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "12px",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "14px",
                              color: "rgba(255,255,255,0.9)",
                              fontWeight: 600,
                              margin: 0,
                            }}
                          >
                            Total Filings
                          </p>
                          <FileText size={24} color="rgba(255,255,255,0.8)" />
                        </div>
                        <p
                          style={{
                            fontSize: "36px",
                            fontWeight: 800,
                            color: "white",
                            margin: 0,
                          }}
                        >
                          {monthlyData?.summary.total_filings.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        borderRadius: "16px",
                        padding: "24px",
                        boxShadow: "0 8px 30px rgba(240, 147, 251, 0.3)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "12px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            color: "rgba(255,255,255,0.9)",
                            fontWeight: 600,
                            margin: 0,
                          }}
                        >
                          Avg Per Day
                        </p>
                        <Calendar size={24} color="rgba(255,255,255,0.8)" />
                      </div>
                      <p
                        style={{
                          fontSize: "36px",
                          fontWeight: 800,
                          color: "white",
                          margin: 0,
                        }}
                      >
                        {monthlyData?.summary.avg_filings_per_day.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3">
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                        borderRadius: "16px",
                        padding: "24px",
                        boxShadow: "0 8px 30px rgba(79, 172, 254, 0.3)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "12px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "14px",
                            color: "rgba(255,255,255,0.9)",
                            fontWeight: 600,
                            margin: 0,
                          }}
                        >
                          Peak Day Filings
                        </p>
                        <ArrowUp size={24} color="rgba(255,255,255,0.8)" />
                      </div>
                      <p
                        style={{
                          fontSize: "36px",
                          fontWeight: 800,
                          color: "white",
                          margin: 0,
                        }}
                      >
                        {monthlyData?.summary.peak_day.filings.toLocaleString()}
                      </p>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "rgba(255,255,255,0.8)",
                          margin: "4px 0 0 0",
                        }}
                      >
                        on{" "}
                        {new Date(
                          monthlyData?.summary.peak_day.date
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Line Chart */}
                <div style={{ marginBottom: "32px" }}>
               { loadingMonthly ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (

                 <ResponsiveContainer width="100%" height={450}>
                    <AreaChart data={monthlyData?.daily_breakdown}>
                      <defs>
                        <linearGradient
                          id="colorFilings"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#667eea"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#667eea"
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 13, fill: "#64748b" }}
                        tickFormatter={(value) => new Date(value).getDate()}
                      />
                      <YAxis tick={{ fontSize: 13, fill: "#64748b" }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: "14px", fontWeight: 600 }}
                      />
                      <Area
                        type="monotone"
                        dataKey="total_filings"
                        stroke="#667eea"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorFilings)"
                        name="Daily Filings"
                      />
                    </AreaChart>
                  </ResponsiveContainer>)}
                </div>

                {/* Status and Class Distribution */}
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <div
                      style={{
                        padding: "20px",
                        background: "#f8fafc",
                        borderRadius: "12px",
                        border: "2px solid #e5e7eb",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: 700,
                          marginBottom: "20px",
                          color: "#1e293b",
                        }}
                      >
                        Status Distribution
                      </h3>
                      <ResponsiveContainer width="100%" height={340}>
                        <PieChart>
                          <defs>
                            {monthlyData?.status_distribution.map(
                              (entry, index) => (
                                <filter
                                  key={`shadow-${index}`}
                                  id={`shadow-status-${index}`}
                                  height="200%"
                                >
                                  <feDropShadow
                                    dx="0"
                                    dy="2"
                                    stdDeviation="3"
                                    floodOpacity="0.3"
                                  />
                                </filter>
                              )
                            )}
                          </defs>
                          <Pie
                            data={monthlyData?.status_distribution}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={95}
                            dataKey="count"
                            paddingAngle={2}
                            label={({
                              cx,
                              cy,
                              midAngle,
                              innerRadius,
                              outerRadius,
                              status,
                              percentage,
                            }) => {
                              const RADIAN = Math.PI / 180;
                              const radius = outerRadius + 30;
                              const x =
                                cx + radius * Math.cos(-midAngle * RADIAN);
                              const y =
                                cy + radius * Math.sin(-midAngle * RADIAN);

                              // Only show label if percentage is significant enough
                              if (percentage < 1) return null;

                              return (
                                <text
                                  x={x}
                                  y={y}
                                  fill="#1e293b"
                                  textAnchor={x > cx ? "start" : "end"}
                                  dominantBaseline="central"
                                  style={{
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    textShadow:
                                      "0 1px 2px rgba(255,255,255,0.9)",
                                  }}
                                >
                                  {`${status.substring(0, 20)}${
                                    status.length > 20 ? "..." : ""
                                  }`}
                                  <tspan
                                    x={x}
                                    dy="1.2em"
                                    style={{
                                      fontWeight: "700",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {`${percentage}%`}
                                  </tspan>
                                </text>
                              );
                            }}
                            labelLine={{
                              stroke: "#94a3b8",
                              strokeWidth: 1.5,
                              strokeDasharray: "3 3",
                            }}
                            animationBegin={0}
                            animationDuration={800}
                            animationEasing="ease-out"
                            activeShape={{
                              outerRadius: 105,
                              strokeWidth: 3,
                              stroke: "#fff",
                            }}
                          >
                            {monthlyData?.status_distribution.map(
                              (entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                  filter={`url(#shadow-status-${index})`}
                                  style={{
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                  }}
                                />
                              )
                            )}
                          </Pie>
                          <Tooltip
                            formatter={(value, name) => [
                              `${value.toLocaleString()} filings`,
                              name,
                            ]}
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.98)",
                              border: "2px solid #e5e7eb",
                              borderRadius: "12px",
                              boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                              padding: "12px 16px",
                            }}
                            itemStyle={{
                              color: "#1e293b",
                              fontWeight: "600",
                              fontSize: "13px",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>

                      {/* Legend for small percentages */}
                      <div
                        style={{
                          marginTop: "16px",
                          padding: "12px",
                          background: "#f8fafc",
                          borderRadius: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(200px, 1fr))",
                            gap: "8px",
                          }}
                        >
                          {monthlyData?.status_distribution
                            .filter((item) => item.percentage < 10)
                            ?.map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    borderRadius: "3px",
                                    backgroundColor:
                                      COLORS[
                                        monthlyData?.status_distribution.indexOf(
                                          item
                                        ) % COLORS.length
                                      ],
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#64748b",
                                    fontWeight: 500,
                                  }}
                                >
                                  {item.status}: {item.percentage}%
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 mb-3">
                    <div
                      style={{
                        padding: "20px",
                        background: "#f8fafc",
                        borderRadius: "12px",
                        border: "2px solid #e5e7eb",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "20px",
                          fontWeight: 700,
                          marginBottom: "20px",
                          color: "#1e293b",
                        }}
                      >
                        Top Classes
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={monthlyData?.class_distribution}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e5e7eb"
                          />
                          <XAxis
                            dataKey="class"
                            tick={{ fontSize: 12, fill: "#64748b" }}
                          />
                          <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar
                            dataKey="count"
                            name="Filings"
                            radius={[8, 8, 0, 0]}
                          >
                            {monthlyData?.class_distribution.map(
                              (entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              )
                            )}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filings vs Approvals and Objections vs Oppositions */}
          <div className="row mb-4">
            <div className="col-lg-12 mb-4">
              <div
                className="card h-100"
                style={{
                  borderRadius: "16px",
                  border: "2px solid #e5e7eb",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                }}
              >
                <div className="card-body" style={{ padding: "28px" }}>
                  <div style={{ marginBottom: "24px" }}>
                    <h2
                      style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        marginBottom: "8px",
                        color: "#1e293b",
                      }}
                    >
                      Filings vs Approvals by Year ({yearRange.from} -{" "}
                      {yearRange.to})
                    </h2>
                    <p
                      style={{ color: "#64748b", margin: 0, fontSize: "15px" }}
                    >
                      Overall approval rate:{" "}
                      <span style={{ fontWeight: 700, color: "#10b981" }}>
                        {filingsApprovalsData?.summary.overall_approval_rate}%
                      </span>
                    </p>
                  </div>
               { loadingFilings ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (  <ResponsiveContainer width="100%" height={420}>
                    <ComposedChart
                      data={filingsApprovalsData?.yearly_breakdown}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="year"
                        tick={{
                          fontSize: 13,
                          fill: "#64748b",
                          fontWeight: 600,
                        }}
                      />
                      <YAxis tick={{ fontSize: 13, fill: "#64748b" }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: "14px", fontWeight: 600 }}
                      />
                      <Bar
                        dataKey="total_filings"
                        fill="#fbbf24"
                        name="Total Filings"
                        radius={[10, 10, 0, 0]}
                        barSize={40}
                      />
                      <Bar
                        dataKey="registered"
                        fill="#10b981"
                        name="Registered"
                        radius={[10, 10, 0, 0]}
                        barSize={40}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>)}
                </div>
              </div>
            </div>

            <div className="col-lg-12 mb-4">
              <div
                className="card h-100"
                style={{
                  borderRadius: "16px",
                  border: "2px solid #e5e7eb",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                }}
              >
                <div className="card-body" style={{ padding: "28px" }}>
                  <div style={{ marginBottom: "24px" }}>
                    <h2
                      style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        marginBottom: "8px",
                        color: "#1e293b",
                      }}
                    >
                      Objections vs Oppositions by Class ({yearRange.from} -{" "}
                      {yearRange.to})
                    </h2>
                    <p
                      style={{ color: "#64748b", margin: 0, fontSize: "15px" }}
                    >
                      Top trademark classes analysis
                    </p>
                  </div>
                {loadingObjection ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (  <ResponsiveContainer width="100%" height={420}>
                    <BarChart
                      data={objectionOppositionData?.classwise_breakdown}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="class"
                        tick={{
                          fontSize: 13,
                          fill: "#64748b",
                          fontWeight: 600,
                        }}
                      />
                      <YAxis tick={{ fontSize: 13, fill: "#64748b" }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: "14px", fontWeight: 600 }}
                      />
                      <Bar
                        dataKey="objections"
                        fill="#ef4444"
                        name="Objections"
                        radius={[10, 10, 0, 0]}
                        barSize={30}
                      />
                      <Bar
                        dataKey="oppositions"
                        fill="#f97316"
                        name="Oppositions"
                        radius={[10, 10, 0, 0]}
                        barSize={30}
                      />
                    </BarChart>
                  </ResponsiveContainer>)}
                </div>
              </div>
            </div>
          </div>

          {/* Trademark Filings by State */}
          <div style={{ marginBottom: "32px" }}>
            <div
              className="card"
              style={{
                borderRadius: "16px",
                border: "2px solid #e5e7eb",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              }}
            >
              <div className="card-body" style={{ padding: "28px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "24px",
                  }}
                >
                  <MapPin size={28} color="#3b82f6" />
                  <div>
                    <h2
                      style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        marginBottom: "4px",
                        color: "#1e293b",
                      }}
                    >
                      Trademark Filings by State ({yearRange.from} -{" "}
                      {yearRange.to})
                    </h2>
                    <p
                      style={{ color: "#64748b", margin: 0, fontSize: "15px" }}
                    >
                      Geographical distribution of trademark applications
                    </p>
                  </div>
                </div>
               {loadingState ? (
              <Skeleton variant="rectangular" height={400} />
            ) : ( <ResponsiveContainer width="100%" height={450}>
                  <BarChart
                    data={stateData?.statewise_breakdown}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 13, fill: "#64748b" }}
                    />
                    <YAxis
                      dataKey="state"
                      type="category"
                      width={180}
                      tick={{ fontSize: 12, fill: "#1e293b", fontWeight: 600 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{ fontSize: "14px", fontWeight: 600 }}
                    />
                    <Bar
                      dataKey="total_filings"
                      fill="#3b82f6"
                      name="Total Filings"
                      radius={[0, 10, 10, 0]}
                      barSize={35}
                    >
                      {stateData?.statewise_breakdown.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>)}
              </div>
            </div>
          </div>

          {/* Top Attorneys and Brands Charts */}
          <div className="row mb-4">
            <div className="col-lg-6 mb-4">
              <div
                className="card h-100"
                style={{
                  borderRadius: "16px",
                  border: "2px solid #e5e7eb",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                }}
              >
                <div className="card-body" style={{ padding: "28px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Users size={28} color="white" />
                    </div>
                    <h2
                      style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        margin: 0,
                        color: "#1e293b",
                      }}
                    >
                      Top 10 Attorneys by Filings
                    </h2>
                  </div>
                 { loadingTopAttorneys ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (<ResponsiveContainer width="100%" height={450}>
                    <BarChart
                      data={topAttorneys?.aggregations}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 13, fill: "#64748b" }}
                      />
                      <YAxis
                        dataKey="attorney_name"
                        type="category"
                        width={160}
                        tick={{
                          fontSize: 11,
                          fill: "#1e293b",
                          fontWeight: 500,
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="doc_count"
                        name="Filings"
                        radius={[0, 10, 10, 0]}
                        barSize={32}
                      >
                        {topAttorneys?.aggregations.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>)}
                </div>
              </div>
            </div>

            <div className="col-lg-6 mb-4">
              <div
                className="card h-100"
                style={{
                  borderRadius: "16px",
                  border: "2px solid #e5e7eb",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                }}
              >
                <div className="card-body" style={{ padding: "28px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Building2 size={28} color="white" />
                    </div>
                    <h2
                      style={{
                        fontSize: "24px",
                        fontWeight: 700,
                        margin: 0,
                        color: "#1e293b",
                      }}
                    >
                      Top 10 Brands by Filings
                    </h2>
                  </div>
                 { loadingTopBrands ? (
              <Skeleton variant="rectangular" height={400} />
            ) : (<ResponsiveContainer width="100%" height={450}>
                    <BarChart data={topBrands?.aggregations} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 13, fill: "#64748b" }}
                      />
                      <YAxis
                        dataKey="attorney_name"
                        type="category"
                        width={160}
                        tick={{
                          fontSize: 11,
                          fill: "#1e293b",
                          fontWeight: 500,
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar
                        dataKey="doc_count"
                        name="Filings"
                        radius={[0, 10, 10, 0]}
                        barSize={32}
                      >
                        {topBrands?.aggregations.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>)}
                </div>
              </div>
            </div>
          </div>

          {/* Top 50 Tables */}
          <div>
            <div
              className="card"
              style={{
                borderRadius: "16px",
                border: "2px solid #e5e7eb",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
              }}
            >
              <div className="card-body" style={{ padding: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "28px",
                  }}
                >
                  <Award size={32} color="#f59e0b" />
                  <h2
                    style={{
                      fontSize: "28px",
                      fontWeight: 700,
                      margin: 0,
                      color: "#1e293b",
                    }}
                  >
                    Detailed Rankings
                  </h2>
                </div>

                <ul
                  className="nav nav-tabs mb-4"
                  role="tablist"
                  style={{ borderBottom: "3px solid #e5e7eb" }}
                >
                  <li className="nav-item">
                    <button
                      className={`nav-link ${tabValue === 0 ? "active" : ""}`}
                      onClick={() => setTabValue(0)}
                      style={{
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: "16px",
                        padding: "14px 28px",
                        color: tabValue === 0 ? "#3b82f6" : "#64748b",
                        borderBottom:
                          tabValue === 0 ? "3px solid #3b82f6" : "none",
                        background: tabValue === 0 ? "#eff6ff" : "transparent",
                        borderRadius: "8px 8px 0 0",
                        transition: "all 0.3s",
                      }}
                    >
                      Top 50 Attorneys ({yearRange.from} - {yearRange.to})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link ${tabValue === 1 ? "active" : ""}`}
                      onClick={() => setTabValue(1)}
                      style={{
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: "16px",
                        padding: "14px 28px",
                        color: tabValue === 1 ? "#8b5cf6" : "#64748b",
                        borderBottom:
                          tabValue === 1 ? "3px solid #8b5cf6" : "none",
                        background: tabValue === 1 ? "#faf5ff" : "transparent",
                        borderRadius: "8px 8px 0 0",
                        transition: "all 0.3s",
                      }}
                    >
                      Top 50 Brands ({yearRange.from} - {yearRange.to})
                    </button>
                  </li>
                </ul>

                {tabValue === 0 && (
                  <div className="table-responsive">
                    <table
                      className="table table-hover"
                      style={{ marginBottom: 0 }}
                    >
                      <thead
                        style={{
                          background:
                            "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                          borderBottom: "3px solid #3b82f6",
                        }}
                      >
                        <tr>
                          <th
                            style={{
                              fontWeight: 800,
                              width: "100px",
                              padding: "18px",
                              fontSize: "15px",
                              color: "#1e40af",
                            }}
                          >
                            Rank
                          </th>
                          <th
                            style={{
                              fontWeight: 800,
                              padding: "18px",
                              fontSize: "15px",
                              color: "#1e40af",
                            }}
                          >
                            Attorney
                          </th>
                          <th
                            style={{
                              fontWeight: 800,
                              textAlign: "right",
                              padding: "18px",
                              fontSize: "15px",
                              color: "#1e40af",
                            }}
                          >
                            Total Filings
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topAttorneys50?.aggregations.map((attorney, index) => (
                          <tr
                            key={index}
                            style={{
                              transition: "all 0.2s",
                              backgroundColor:
                                index % 2 === 0 ? "#ffffff" : "#f8fafc",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "#eff6ff")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                index % 2 === 0 ? "#ffffff" : "#f8fafc")
                            }
                          >
                            <td style={{ padding: "18px" }}>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "44px",
                                  height: "44px",
                                  borderRadius: "50%",
                                  fontWeight: 800,
                                  fontSize: "15px",
                                  background:
                                    index < 3
                                      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                      : "#e5e7eb",
                                  color: index < 3 ? "white" : "#4b5563",
                                  boxShadow:
                                    index < 3
                                      ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                                      : "none",
                                }}
                              >
                                {index < 3 && "🏆 "}#{index + 1}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "18px",
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1e293b",
                              }}
                            >
                              <Link to={`/cms/manage-attorney-data/${encodeURIComponent(attorney.key)}`}>{attorney.attorney_name}</Link>
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: 700,
                                padding: "18px",
                                fontSize: "16px",
                                color: "#3b82f6",
                              }}
                            >
                              {attorney.doc_count.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {tabValue === 1 && (
                  <div className="table-responsive">
                    <table
                      className="table table-hover"
                      style={{ marginBottom: 0 }}
                    >
                      <thead
                        style={{
                          background:
                            "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
                          borderBottom: "3px solid #8b5cf6",
                        }}
                      >
                        <tr>
                          <th
                            style={{
                              fontWeight: 800,
                              width: "100px",
                              padding: "18px",
                              fontSize: "15px",
                              color: "#6b21a8",
                            }}
                          >
                            Rank
                          </th>
                          <th
                            style={{
                              fontWeight: 800,
                              padding: "18px",
                              fontSize: "15px",
                              color: "#6b21a8",
                            }}
                          >
                            Brand
                          </th>
                          <th
                            style={{
                              fontWeight: 800,
                              textAlign: "right",
                              padding: "18px",
                              fontSize: "15px",
                              color: "#6b21a8",
                            }}
                          >
                            Total Filings
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topBrands50?.aggregations.map((brand, index) => (
                          <tr
                            key={index}
                            style={{
                              transition: "all 0.2s",
                              backgroundColor:
                                index % 2 === 0 ? "#ffffff" : "#f8fafc",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "#faf5ff")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                index % 2 === 0 ? "#ffffff" : "#f8fafc")
                            }
                          >
                            <td style={{ padding: "18px" }}>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "44px",
                                  height: "44px",
                                  borderRadius: "50%",
                                  fontWeight: 800,
                                  fontSize: "15px",
                                  background:
                                    index < 3
                                      ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                                      : "#e5e7eb",
                                  color: index < 3 ? "white" : "#4b5563",
                                  boxShadow:
                                    index < 3
                                      ? "0 4px 12px rgba(240, 147, 251, 0.3)"
                                      : "none",
                                }}
                              >
                                {index < 3 && "🏆 "}#{index + 1}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "18px",
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1e293b",
                              }}
                            >
                             <Link to={`/cms/manage-proprietor-data/${encodeURIComponent(brand.attorney_name)}`}>{extractProprietorName(brand.attorney_name)}</Link>
                            </td>
                            <td
                              style={{
                                textAlign: "right",
                                fontWeight: 700,
                                padding: "18px",
                                fontSize: "16px",
                                color: "#8b5cf6",
                              }}
                            >
                              {brand.doc_count.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageTrademarkTrends;
